import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { formatXAF } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import ContactFooter from "@/components/layout/ContactFooter";
import {
  Sprout, Fish, Package, ShoppingCart, Truck, CreditCard, BarChart3,
  Bot, Languages, LogIn, QrCode, CloudSun, ShieldCheck, Check,
} from "lucide-react";

const FEATURES = [
  { icon: Sprout, key: "feat_agri" },
  { icon: Fish, key: "feat_pisci" },
  { icon: Package, key: "feat_stock" },
  { icon: ShoppingCart, key: "feat_market" },
  { icon: Truck, key: "feat_logi" },
  { icon: CreditCard, key: "feat_pay" },
  { icon: BarChart3, key: "feat_analytics" },
  { icon: Bot, key: "feat_ai" },
  { icon: CloudSun, key: "feat_weather" },
  { icon: QrCode, key: "feat_qr" },
  { icon: ShieldCheck, key: "feat_roles" },
];

function PricingSection() {
  const { t } = useI18n();
  const [plans, setPlans] = useState([]);
  useEffect(() => {
    api.get("/public/license-plans").then((r) => setPlans(r.data)).catch(() => {});
  }, []);

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {plans.map((p, i) => (
        <Card key={p.key} className={`border-border ${i === 1 ? "ring-2 ring-primary relative" : ""}`} data-testid={`plan-${p.key}`}>
          {i === 1 && (
            <Badge className="absolute -top-2.5 left-1/2 -translate-x-1/2" data-testid={`plan-popular-${p.key}`}>{t("popular")}</Badge>
          )}
          <CardContent className="p-6 space-y-4 text-left">
            <div className="font-bold text-lg" data-testid={`plan-label-${p.key}`}>{p.label}</div>
            <div className="text-3xl font-bold">
              {formatXAF(p.price)}<span className="text-sm font-normal text-muted-foreground">{t("per_month")}</span>
            </div>
            <div className="text-xs text-muted-foreground">{p.max_users} {t("users_included")}</div>
            <div className="label-eyebrow">{t("modules_included")}</div>
            <ul className="space-y-1.5 text-sm">
              {p.modules.map((m) => (
                <li key={m} className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-primary shrink-0" />{t(`mod_${m}`)}
                </li>
              ))}
            </ul>
            <a href="#contact">
              <Button variant={i === 1 ? "default" : "outline"} className="w-full" data-testid={`plan-cta-${p.key}`}>{t("contact_us")}</Button>
            </a>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function ContactSection() {
  const { t } = useI18n();
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/public/contact", form);
      toast.success(t("message_sent"));
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || t("error"));
    } finally {
      setSending(false);
    }
  }

  return (
    <section id="contact" className="max-w-2xl mx-auto scroll-mt-24" data-testid="contact-section">
      <div className="label-eyebrow text-center mb-2">{t("contact_title")}</div>
      <p className="text-sm text-muted-foreground text-center mb-6">{t("contact_sub")}</p>
      <Card className="border-border">
        <CardContent className="p-6">
          <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
            <div className="space-y-1.5">
              <Label htmlFor="contact-name">{t("name")}</Label>
              <Input id="contact-name" data-testid="contact-name" required value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="contact-email-input">{t("email")}</Label>
              <Input id="contact-email-input" type="email" data-testid="contact-email-input" required value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="contact-message">{t("message")}</Label>
              <Textarea id="contact-message" data-testid="contact-message" required rows={4} value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })} />
            </div>
            <Button type="submit" className="w-full" disabled={sending} data-testid="contact-submit">
              {sending ? "…" : t("send")}
            </Button>
          </form>
        </CardContent>
      </Card>
    </section>
  );
}

export default function Decouvrir() {
  const { t, lang, setLang } = useI18n();
  const { user } = useAuth();
  const isGuest = user && user.role === "invité";

  return (
    <div className="min-h-screen bg-background animated-texture grain-overlay" data-testid="discover-page">
      <header className="border-b border-border bg-secondary/60 sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo-agrinova.png" alt="AgriNova" className="w-10 h-10 rounded-lg object-cover" />
            <div>
              <div className="font-bold">AgriNova</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{t("slogan")}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setLang(lang === "fr" ? "en" : "fr")}
              className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-primary border border-primary/30 rounded-full px-3 py-1.5"
              data-testid="lang-toggle-discover"
            >
              <Languages className="w-3.5 h-3.5" />{lang === "fr" ? "EN" : "FR"}
            </button>
            <Link to={isGuest ? "/login" : "/"}>
              <Button size="sm" data-testid="discover-login-btn">
                <LogIn className="w-4 h-4 mr-1" />{isGuest ? t("login") : t("dashboard")}
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {isGuest && (
        <div className="bg-primary text-primary-foreground text-center text-sm py-2" data-testid="guest-banner">
          {t("guest_banner")}{" "}
          <Link to="/login" className="underline font-semibold" data-testid="guest-create-account">
            {t("create_account")}
          </Link>
        </div>
      )}

      <main className="max-w-6xl mx-auto px-4 py-10 space-y-14">
        <section className="text-center space-y-5">
          <Badge variant="secondary" className="mx-auto" data-testid="discover-badge">{t("discover_badge")}</Badge>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight max-w-3xl mx-auto">
            {t("discover_title")}
          </h1>
          <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto">{t("discover_sub")}</p>
          <div className="flex items-center justify-center gap-3 pt-2">
            <Link to="/commander"><Button size="lg" data-testid="discover-order-btn">{t("order_now")}</Button></Link>
            <Link to="/login"><Button size="lg" variant="outline" data-testid="discover-signup-btn">{t("login")}</Button></Link>
          </div>
        </section>

        <section>
          <div className="label-eyebrow text-center mb-6">{t("discover_features")}</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 stagger">
            {FEATURES.map(({ icon: Icon, key }) => (
              <Card key={key} className="card-hover border-border" data-testid={`feature-${key}`}>
                <CardContent className="p-5 flex gap-4">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                    <Icon className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="font-semibold">{t(`${key}_title`)}</div>
                    <div className="text-sm text-muted-foreground mt-1">{t(`${key}_desc`)}</div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        <section>
          <div className="label-eyebrow text-center mb-6">{t("pricing")}</div>
          <PricingSection />
        </section>

        <section className="rounded-2xl bg-primary text-primary-foreground p-8 md:p-12 text-center relative overflow-hidden">
          <div className="texture-topo-overlay" />
          <div className="relative z-10 space-y-4">
            <h2 className="text-2xl md:text-3xl font-bold">{t("discover_cta_title")}</h2>
            <p className="text-primary-foreground/80 max-w-xl mx-auto">{t("discover_cta_sub")}</p>
            <Link to="/commander">
              <Button size="lg" variant="secondary" data-testid="discover-cta-btn">{t("order_now")}</Button>
            </Link>
          </div>
        </section>

        <ContactSection />
      </main>

      <ContactFooter />
    </div>
  );
}
